import type { Patient } from '../../../types';
import { LabPanelSection } from './LabPanelSection';
import { Markdown } from '../../../components/Markdown/Markdown';
import './LabResultsSummary.scss';

interface LabResultsSummaryProps {
  patient: Patient;
}

/**
 * Render structured laboratory cards, interactive trend graphs, and interpretation.
 *
 * @param patient - The patient hydrated with `labResults` from the document summary
 */
export function LabResultsSummary({ patient }: LabResultsSummaryProps) {
  const labResults = patient.labResults;
  const points = labResults?.clinicalInterpretation || [];
  const completeBloodCount = labResults?.completeBloodCount ?? [];
  const metabolicPanel = labResults?.metabolicPanel ?? [];
  const tumorMarkers = labResults?.tumorMarkers ?? [];
  const hasPanels = Boolean(
    completeBloodCount.length > 0 ||
      metabolicPanel.length > 0 ||
      tumorMarkers.length > 0,
  );

  if (!labResults || (!hasPanels && points.length === 0 && !labResults.narrative)) {
    return (
      <div className="lab-results-report">
        <div className="lab-results-section">
          <p className="lab-results-empty">
            No laboratory results available for this patient.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lab-results-report">
      <LabPanelSection title="Metabolic Panel" items={metabolicPanel} />
      <LabPanelSection title="Tumor Markers" items={tumorMarkers} />
      <LabPanelSection title="Complete Blood Count" items={completeBloodCount} />

      {(points.length > 0 || labResults.narrative) && (
        <section className="lab-results-section">
        <div className="clinical-interpretation-box">
          <h3 className="clinical-interpretation-title">Laboratory Trend Summary</h3>
          {labResults.narrative ? (
            <Markdown className="clinical-interpretation-narrative">{labResults.narrative}</Markdown>
          ) : (
            <ul className="clinical-interpretation-list">
              {points.map((point, index) => (
                <li key={index}>{point}</li>
              ))}
            </ul>
          )}
        </div>
        </section>
      )}
    </div>
  );
}
